import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import ContentCard from "@/components/ContentCard";
import EmptyState from "@/components/EmptyState";
import MediaEmbed from "@/components/MediaEmbed";
import AudioPlayer from "@/components/AudioPlayer";
import Attachments from "@/components/Attachments";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const META = {
  writing: { eyebrowKey: "nav_writing", titleKey: "nav_writing", descKey: "writing_desc" },
  video: { eyebrowKey: "nav_video", titleKey: "nav_video", descKey: "video_desc" },
  short: { eyebrowKey: "nav_short", titleKey: "nav_short", descKey: "short_desc" },
  audio: { eyebrowKey: "nav_audio", titleKey: "nav_audio", descKey: "audio_desc" },
};

export default function Section({ type }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    api.get("/content", { params: { type, lang } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [type, lang]);

  useEffect(() => { load(); }, [load]);

  const meta = META[type];

  const handleOpen = (item) => {
    if (item.locked) { navigate("/premium"); return; }
    setActive(item);
  };

  return (
    <div data-testid={`section-${type}`}>
      <PageHeader eyebrow={t(meta.eyebrowKey)} title={t(meta.titleKey)} desc={t(meta.descKey)} />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 pb-24">
        {items === null ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => <div key={i} className="aspect-[4/3] rounded-xl bg-[#FFFFFF] animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState title={t("empty_title")} desc={t("empty_desc")} />
        ) : type === "audio" ? (
          <div className="space-y-4 max-w-3xl">
            {items.map((item) => (
              <div key={item.id} className="rounded-2xl border border-[rgba(61,50,56,0.08)] bg-[#FFFFFF] p-5" data-testid={`audio-item-${item.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F3E8E2] shrink-0">
                    {item.cover_path ? <img src={mediaUrl(item.cover_path)} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center font-serif text-2xl text-[rgba(198,137,158,0.3)]">{item.title.charAt(0)}</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-xl text-[#3D3238] flex items-center gap-2">
                      {item.title}
                      {item.is_premium && <Lock className="w-3.5 h-3.5 text-[#C6899E]" />}
                    </h3>
                    {item.excerpt && <p className="text-sm text-[#8C7E83] mt-1 line-clamp-2">{item.excerpt}</p>}
                  </div>
                </div>
                <div className="mt-4">
                  {item.locked ? (
                    <button onClick={() => navigate("/premium")} data-testid={`unlock-${item.id}`}
                      className="inline-flex items-center gap-2 text-sm text-[#C6899E] border border-[rgba(198,137,158,0.3)] rounded-full px-4 py-2 hover:bg-[rgba(198,137,158,0.1)]">
                      <Lock className="w-3.5 h-3.5" /> {t("unlock")}
                    </button>
                  ) : item.media_kind === "upload" && item.media_path ? (
                    <AudioPlayer path={item.media_path} />
                  ) : (
                    <MediaEmbed kind={item.media_kind} url={item.media_url} path={item.media_path} />
                  )}
                </div>
                {!item.locked && <Attachments files={item.attachments} label="Allegati" />}
              </div>
            ))}
          </div>
        ) : (
          <div className={type === "short"
            ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6"
            : "grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"}>
            {items.map((item, i) => (
              <ContentCard key={item.id} item={item} index={i}
                vertical={type === "short"}
                showPlay={type === "video" || type === "short"}
                to={type === "writing" ? `/writing/${item.slug}` : undefined}
                onClick={type === "writing" ? undefined : () => handleOpen(item)} />
            ))}
          </div>
        )}
      </div>

      {/* Video / Short modal */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className={`bg-[#FBF5F1] border-[rgba(198,137,158,0.2)] p-0 overflow-hidden ${active?.type === "short" ? "max-w-md" : "max-w-4xl"}`}>
          {active && (
            <div>
              <MediaEmbed kind={active.media_kind} url={active.media_url} path={active.media_path}
                vertical={active.type === "short"} poster={mediaUrl(active.cover_path)} />
              <div className="p-6">
                <h3 className="font-serif text-2xl text-[#3D3238]">{active.title}</h3>
                {active.body && <p className="mt-3 text-[#8C7E83] leading-relaxed whitespace-pre-wrap">{active.body}</p>}
                <Attachments files={active.attachments} label="Allegati" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
